import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { webEnvConfig } from '@config';
import { container } from '@containers';
import { LoginResponse } from '@libs/types';
import { AuthStore } from '@stores';

import { DI_TOKENS } from '../constants';

// Create axios instance with default configuration
export const apiClient: AxiosInstance = axios.create({
  baseURL: webEnvConfig.api.baseURL,
  timeout: webEnvConfig.api.timeout,
  headers: webEnvConfig.api.headers,
});

// Token refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

const onRefreshFailed = () => {
  refreshSubscribers = [];
};

/**
 * Attempt to refresh the access token using the refresh token
 */
const refreshAccessToken = async (authStore: AuthStore): Promise<string | null> => {
  const refreshToken = authStore.refreshToken;
  if (!refreshToken) {
    return null;
  }

  try {
    const { data } = await axios.post<LoginResponse>(
      `${webEnvConfig.api.baseURL}/api/auth/refresh`,
      { refreshToken },
      { headers: webEnvConfig.api.headers },
    );

    if (!data) {
      return null;
    }

    // Update auth store with new tokens
    authStore.setToken(data.accessToken);
    authStore.setRefreshToken(data.refreshToken);
    authStore.setUserProfile(data.userInfo);

    return data.accessToken;
  } catch {
    return null;
  }
};

// Request interceptor
apiClient.interceptors.request.use(
  async (config) => {
    // Log requests in development
    if (webEnvConfig.env === 'development') {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    const authStore = await container.getAsync<AuthStore>(DI_TOKENS.AuthStore);
    const token = authStore.token;
    // Add auth token if available
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add current language to Accept-Language header
    config.headers['Accept-Language'] = localStorage.getItem('i18nextLng') || 'en';

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  },
);

// Add request interceptor for file uploads
apiClient.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    config.headers['Content-Type'] = 'multipart/form-data';
  }
  return config;
});

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log responses in development
    if (webEnvConfig.env === 'development') {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Log errors in development
    if (webEnvConfig.env === 'development') {
      console.error('❌ API Error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
        url: error.config?.url,
      });
    }

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh or logout endpoints
      const url = originalRequest.url || '';
      if (url.includes('/auth/refresh') || url.includes('/auth/logout')) {
        const authStore = await container.getAsync<AuthStore>(DI_TOKENS.AuthStore);
        authStore.clearAuth();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(originalRequest));
          });
          // If refresh fails while waiting, reject
          setTimeout(() => {
            if (!isRefreshing) {
              reject(error);
            }
          }, 60000); // 60s timeout for waiting
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const authStore = await container.getAsync<AuthStore>(DI_TOKENS.AuthStore);
        const newToken = await refreshAccessToken(authStore);

        if (newToken) {
          onTokenRefreshed(newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } else {
          // Refresh failed - clear auth and reject
          onRefreshFailed();
          authStore.clearAuth();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        onRefreshFailed();
        const authStore = await container.getAsync<AuthStore>(DI_TOKENS.AuthStore);
        authStore.clearAuth();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
